import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToolbar } from '../../../shared/hooks/Toolbar';
import { useDataTable } from '../../../shared/hooks/DataTable';
import { useDialogCrup } from '../../../shared/hooks/DialogCrup';
import { TenantSettingService as Service } from '../tham-so-he-thong/TenantSettingService';
import { RegisterInvoiceService } from '../../../service/invoice/RegisterInvoiceService';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { OverlayPanel } from 'primereact/overlaypanel';
import { ToastService } from '../../../AppToast';
import { ASYNC_FLOW_CANCELLED } from '../../../shared/utils/asyncFlow';

import { FormatDisplay } from '../../../shared/format';
import { useTranslation } from '../../../shared/language';

type AutoSignJobCondition = {
  propertyName: string;
  operation: string;
  value: string;
};

const createAutoSignJobDefaultConditions = (): AutoSignJobCondition[] => [
  { propertyName: 'creationTime', operation: '', value: '' },
  { propertyName: 'invoiceDate', operation: '=', value: '' },
];

// Dropdown cần token để hiển thị nhãn, còn payload FE gửi ngày tương đối bằng offset số.
const normalizeAutoSignJobInvoiceDateValue = (value: any): string => {
  if (value === 0 || value === '0') return 'today';
  if (value === -1 || value === '-1') return 'yesterday';
  return value === null || value === undefined ? '' : String(value);
};

const normalizeAutoSignJobConditions = (condition: any): AutoSignJobCondition[] => {
  let savedConditions = [];
  try {
    const parsedCondition = typeof condition === 'string' ? JSON.parse(condition) : condition;
    savedConditions = Array.isArray(parsedCondition?.condition) ? parsedCondition.condition : [];
  } catch {
    savedConditions = [];
  }

  const rows = createAutoSignJobDefaultConditions();
  // Hai trường lọc cố định theo nghiệp vụ; ghép dữ liệu cũ theo mã để không đổi vị trí tên trường khi mở lại.
  savedConditions.forEach((savedCondition) => {
    const row = rows.find((condition) => condition.propertyName === savedCondition?.propertyName);
    if (!row) return;
    // Ngày hóa đơn cố định toán tử bằng; ngày tạo giữ toán tử đã lưu để người dùng tiếp tục điều chỉnh.
    row.operation = row.propertyName === 'invoiceDate' ? '=' : savedCondition?.operation || '';
    const savedValue = savedCondition?.value;
    row.value = row.propertyName === 'invoiceDate'
      ? normalizeAutoSignJobInvoiceDateValue(savedValue)
      : savedValue === null || savedValue === undefined ? '' : String(savedValue);
  });
  return rows;
};

// API chỉ lưu giờ dạng chuỗi, còn Calendar cần Date; gắn giờ đã lưu vào ngày hiện tại để giữ đúng hợp đồng dữ liệu.
const parseAutoSignJobConditionTime = (value: string): Date | null => {
  if (!/^(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d$/.test(value)) return null;

  const [hour, minute, second] = value.split(':').map(Number);
  const date = new Date();
  date.setHours(hour, minute, second, 0);
  return date;
};

type AutoSignJobConditionTimeParts = {
  hour: string;
  minute: string;
  second: string;
};

const getAutoSignJobConditionTimeParts = (value: string): AutoSignJobConditionTimeParts => {
  const date = parseAutoSignJobConditionTime(value) || new Date();
  return {
    hour: String(date.getHours()).padStart(2, '0'),
    minute: String(date.getMinutes()).padStart(2, '0'),
    second: String(date.getSeconds()).padStart(2, '0'),
  };
};

const formatAutoSignJobConditionTime = (parts: AutoSignJobConditionTimeParts): string =>
  `${parts.hour}:${parts.minute}:${parts.second}`;

// Tách ba phần giờ để người dùng chỉnh từng giá trị mà vẫn lưu đúng chuỗi thời gian API đang nhận.
const AutoSignJobConditionTimePicker = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) => {
  const overlayRef = useRef<OverlayPanel>(null);
  const [inputValue, setInputValue] = useState(value);
  const [timeParts, setTimeParts] = useState<AutoSignJobConditionTimeParts>(() => getAutoSignJobConditionTimeParts(value));

  useEffect(() => {
    setInputValue(value);
    setTimeParts(getAutoSignJobConditionTimeParts(value));
  }, [value]);

  const updateAutoSignJobTimePart = (part: keyof AutoSignJobConditionTimeParts, rawValue: string) => {
    const nextValue = rawValue.replace(/\D/g, '').slice(0, 2);
    const nextParts = { ...timeParts, [part]: nextValue };
    setTimeParts(nextParts);

    if (nextValue.length !== 2) return;
    const maximum = part === 'hour' ? 23 : 59;
    if (Number(nextValue) > maximum) return;

    const normalizedParts = { ...nextParts, [part]: nextValue.padStart(2, '0') };
    setTimeParts(normalizedParts);
    onChange(formatAutoSignJobConditionTime(normalizedParts));
  };

  const commitAutoSignJobTimePart = (part: keyof AutoSignJobConditionTimeParts) => {
    const rawValue = timeParts[part];
    const maximum = part === 'hour' ? 23 : 59;
    if (!rawValue || Number(rawValue) > maximum) {
      setTimeParts(getAutoSignJobConditionTimeParts(value));
      return;
    }

    const normalizedParts = { ...timeParts, [part]: rawValue.padStart(2, '0') };
    setTimeParts(normalizedParts);
    onChange(formatAutoSignJobConditionTime(normalizedParts));
  };

  const renderAutoSignJobTimePart = (
    part: keyof AutoSignJobConditionTimeParts,
    label: string,
    maximum: number,
  ) => (
    <div className="flex-1">
      <label className="block text-xs text-500 mb-1">{label}</label>
      <InputText
        value={timeParts[part]}
        onChange={(event) => updateAutoSignJobTimePart(part, event.target.value)}
        onBlur={() => commitAutoSignJobTimePart(part)}
        onFocus={(event) => event.currentTarget.select()}
        onKeyDown={(event) => {
          if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            event.preventDefault();
            const direction = event.key === 'ArrowUp' ? 1 : -1;
            const nextValue = (Number(timeParts[part] || 0) + direction + maximum + 1) % (maximum + 1);
            updateAutoSignJobTimePart(part, String(nextValue).padStart(2, '0'));
          }
        }}
        aria-label={label}
        inputMode="numeric"
        maxLength={2}
        className="w-full text-center font-semibold"
      />
    </div>
  );

  const openAutoSignJobTimePicker = (event: React.FocusEvent<HTMLInputElement>) => {
    setTimeParts(getAutoSignJobConditionTimeParts(value));
    overlayRef.current?.show(event, event.currentTarget);
  };

  return (
    <>
      <InputText
        value={inputValue}
        onChange={(event) => {
          const nextValue = event.target.value;
          setInputValue(nextValue);
          if (/^(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d$/.test(nextValue)) onChange(nextValue);
        }}
        onFocus={openAutoSignJobTimePicker}
        onBlur={() => {
          if (inputValue && !/^(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d$/.test(inputValue)) {
            setInputValue(value);
          }
        }}
        placeholder="HH:mm:ss"
        className="w-full"
        style={{ width: '100%' }}
        aria-label="Thời gian tạo hóa đơn"
      />
      <OverlayPanel ref={overlayRef} style={{ width: '300px' }}>
        <div className="flex align-items-center justify-content-between mb-3">
          <div>
            <div className="font-semibold text-900">Chọn thời gian</div>
            <div className="text-xs text-500 mt-1">Giờ trong ngày</div>
          </div>
          <div className="border-round bg-primary-50 text-primary-700 font-semibold px-2 py-1">
            {formatAutoSignJobConditionTime(timeParts)}
          </div>
        </div>
        <div className="flex align-items-end gap-2">
          {renderAutoSignJobTimePart('hour', 'Giờ', 23)}
          <span className="font-semibold text-500 pb-2">:</span>
          {renderAutoSignJobTimePart('minute', 'Phút', 59)}
          <span className="font-semibold text-500 pb-2">:</span>
          {renderAutoSignJobTimePart('second', 'Giây', 59)}
        </div>
        <div className="flex justify-content-end mt-3 pt-2 border-top-1 surface-border">
          <Button
            type="button"
            label="Xong"
            className="p-button-sm p-button-text"
            onClick={() => overlayRef.current?.hide()}
          />
        </div>
      </OverlayPanel>
    </>
  );
};

const buildAutoSignJobCondition = (conditions: AutoSignJobCondition[]): string | undefined => {
  const activeConditions = conditions
    .filter((condition) => condition.propertyName && condition.value &&
      (condition.propertyName === 'invoiceDate' || condition.operation),
    )
    .map(({ propertyName, operation, value }) => ({
      propertyName,
      operation: propertyName === 'invoiceDate' ? '=' : operation,
      // Payload dùng offset số cho ngày tương đối; Dropdown giữ token để hiển thị đúng nhãn.
      value: propertyName === 'invoiceDate'
        ? value === 'today' ? 0 : value === 'yesterday' ? -1 : value
        : value,
    }));
  if (!activeConditions.length) return undefined;

  // Giữ offset ngày dạng số vì ngày áp dụng phụ thuộc thời điểm job chạy, không phải lúc cấu hình.
  return JSON.stringify({ condition: activeConditions });
};

const getAutoSignJobStartHour = (job: any): number | null => {
  const dateValue = job?.timeRunView || job?.timeRun;
  if (!dateValue) return null;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;
  // TimeRunView trong DTO đã cộng 7 giờ; khi chỉ có TimeRun cần quy đổi cùng độ lệch để so giờ người dùng chọn.
  return job?.timeRunView ? date.getHours() : (date.getHours() + 7) % 24;
};

const getAutoSignJobStartSeconds = (job: any): number | null => {
  const dateValue = job?.timeRunView || job?.timeRun;
  if (!dateValue) return null;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;
  const hour = job?.timeRunView ? date.getHours() : (date.getHours() + 7) % 24;
  return hour * 3600 + date.getMinutes() * 60 + date.getSeconds();
};

const getAutoSignJobEndHourValue = (value: any): number | null => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string' && /^(?:[01]\d|2[0-3]):59:59$/.test(value)) {
    return Number(value.slice(0, 2));
  }

  const hour = Number(value);
  // API dùng thang 1–24, còn Dropdown dùng 0–23; giữ số 0 để đọc cấu hình cũ lúc nửa đêm.
  if (Number.isInteger(hour) && hour === 0) return 0;
  if (Number.isInteger(hour) && hour >= 1 && hour <= 24) return hour - 1;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return (date.getHours() + 7) % 24;
};

const serializeAutoSignJobEndHour = (value: any): number | null => {
  // Giờ kết thúc bỏ trống được quy ước là hết ngày, tương ứng 23:59:59 trên giao diện.
  if (value === null || value === undefined || value === '') return 24;
  const hour = Number(value);
  return Number.isInteger(hour) && hour >= 0 && hour <= 23 ? hour + 1 : 24;
};

// Form luôn giữ giờ UI 0–23 để kiểm tra khoảng giờ không bị lệch theo thang API 1–24.
const getAutoSignJobEndHourFromForm = (job: any): number => {
  if (job?.endtHour === null || job?.endtHour === undefined || job?.endtHour === '') return 23;
  const hour = Number(job?.endtHour);
  return Number.isInteger(hour) && hour >= 0 && hour <= 23 ? hour : 23;
};

const getAutoSignJobEndHour = (job: any): number => getAutoSignJobEndHourValue(job?.endtHour) ?? 23;

const hasAutoSignJobEndBeforeStart = (job: any): boolean => {
  const startSeconds = getAutoSignJobStartSeconds(job);
  if (startSeconds === null) return false;

  // Mỗi giờ kết thúc bao gồm đến HH:59:59 nên hai mốc bằng nhau trong cùng ngày vẫn hợp lệ.
  const endSeconds = getAutoSignJobEndHourFromForm(job) * 3600 + 59 * 60 + 59;
  return endSeconds < startSeconds;
};

const hasAutoSignJobOverlappingWindow = (candidate: any, jobs: any[], excludedId?: string): boolean => {
  const candidateStartHour = getAutoSignJobStartHour(candidate);
  if (candidateStartHour === null) return false;
  const candidateEndHour = getAutoSignJobEndHourFromForm(candidate);
  const candidateSerial = candidate.serial || null;

  // Khoảng giờ kết thúc bao gồm đến giây 59, vì vậy hai job cùng chạm một giờ vẫn phải được coi là trùng.
  return jobs.some((job) => {
    if (job.autoSign !== true || String(job.id) === String(excludedId)) return false;
    const jobSerial = job.serial || null;
    if (candidateSerial && jobSerial && candidateSerial !== jobSerial) return false;

    const jobStartHour = getAutoSignJobStartHour(job);
    if (jobStartHour === null) return false;
    const jobEndHour = getAutoSignJobEndHour(job);
    return candidateStartHour <= jobEndHour && jobStartHour <= candidateEndHour;
  });
};

const minuteNextStepOptions = [
  { value: 5, label: "5 phút" },
  { value: 10, label: "10 phút" },
  { value: 15, label: "15 phút" },
  { value: 20, label: "20 phút" },
  { value: 25, label: "25 phút" },
  { value: 30, label: "30 phút" },
  { value: 45, label: "45 phút" },
];

export const List = () => {
  const {t} = useTranslation();
  const [tempAutoSign, setTempAutoSign] = useState(null);
  const [autoSignTimeWindowError, setAutoSignTimeWindowError] = useState('');
  const [isSerialDisable, setIsSerialDisable] = useState(null)
  const [symbol, setSymbol] = useState([]);
  const [conditions, setConditions] = useState<AutoSignJobCondition[]>(createAutoSignJobDefaultConditions);
  const header = t('system.emailMttConfigTitle');
  const dataKey = 'id';
  const [tempSerial, setTempSerial] = useState(null)

  const renderOptions = useCallback(() => {
    let newOptions;
    if (tempAutoSign) {
      newOptions = minuteNextStepOptions.filter((option) => option.value <= 30);

    } else {
      newOptions = minuteNextStepOptions.filter((option) => option.value >= 30 && option.value <= 60);

    }
    return newOptions;
  }, [tempAutoSign]);

  const defaultValue = useMemo(() => {
    return renderOptions()[0].value
  }, [renderOptions])
  const timeRunOptions = Array.from({ length: 24 }, (_, hour) => {
    const adjustedHour = hour < 7 ? hour + 24 - 7 : hour - 7;
    return {
      value: FormatDisplay.date(new Date('2023-07-21').setHours(adjustedHour, 0, 0, 0), 'YYYY-MM-DDTHH:mm:ss'),
      label: `${hour.toString().padStart(2, '0')}:00:00`,
    };
  });
  const endHourOptions = Array.from({ length: 24 }, (_, hour) => ({
    value: hour,
    label: `${hour.toString().padStart(2, '0')}:59:59`,
  }));
  const conditionFieldOptions = [
    { value: 'creationTime', label: t('system.creationTime') },
    { value: 'invoiceDate', label: t('common.invoiceDate') },
  ];
  // Ngày tạo hóa đơn cần đủ toán tử; riêng ngày hóa đơn chỉ đối chiếu bằng theo nghiệp vụ.
  const conditionOperationOptions = [
    { value: '=', label: t('common.equals') },
    { value: '!=', label: t('common.notEquals') },
    { value: '<', label: t('common.lessThan') },
    { value: '<=', label: t('common.lessThanOrEqual') },
    { value: '>', label: t('common.greaterThan') },
    { value: '>=', label: t('common.greaterThanOrEqual') },
  ];
  const invoiceDateValueOptions = [
    { value: 'today', label: t('common.today') },
    { value: 'yesterday', label: t('common.yesterday') },
  ];
  const {
    render: renderDataTable,
    refreshLazyData,
    reloadLazyData,
    selectedItems,
  } = useDataTable({
    header,
    dataKey,
    columns: [
      { field: 'emailTo', header: t('system.notificationEmail'), matchMode: 'contains', minWidth: 160 },
      {
        field: 'timeRunView',
        header: t('system.startTime'),
        minWidth: 160,
        dataType: 'custom',
        customCell: (rowData) => FormatDisplay.date(rowData.timeRunView, 'HH:mm:ss'),
      },
      {
        field: 'endtHour',
        header: t('system.endTime'),
        minWidth: 160,
        dataType: 'custom',
        customCell: (rowData) => rowData.autoSign
          ? `${String(getAutoSignJobEndHour(rowData)).padStart(2, '0')}:59:59`
          : '-',
      },
      { field: 'minuteNextStep', header: t('system.notificationResendTime'), width: 400 },
      { field: 'serial', header: t('common.symbol'), matchMode: 'contains', minWidth: 120 },
      {
        field: 'autoSign',
        header: t('system.feature'),
        dataType: "fromFilterOptions",
        minWidth: 150,
        filterType: 'dropdown',
        matchMode: 'contains',
        filterOptions: [
          { value: true, label: t('system.autoSignInvoice') },
          { value: false, label: t('system.sendNotificationEmail') },
        ],
      },
      {
        field: 'isUse', header: t('catalog.emailUsage'), matchMode: 'equals', filterType: 'boolean', dataType: 'checkbox'
      }
    ],
    actionColumnWidth: 0,
    getList: (params) => {
      return Service.getListJob(params);
    },
  });
  useEffect(() => {
    RegisterInvoiceService.getUsingList({ maxResultCount: 1000, Sorting: 'creationTime', SortType: 'DESCEND' }).then(
      ({ items }) => {
        const _registerInvoices = [];
        items.forEach((item) => {
          item.label = item.symbolCode;
          if (item.signOnly === false) {
            _registerInvoices.push(item.symbolCode);
          }
        });
        setSymbol(_registerInvoices.map((item) => ({
          label: item,
          value: item,
        })));
      }
    );
  }, [t]);
  const updateAutoSignJobCondition = (index: number, field: 'operation' | 'value', value: string) => {
    setConditions((current) => current.map((condition, conditionIndex) =>
      conditionIndex === index
        ? { ...condition, [field]: value }
        : condition,
    ));
  };
  const clearAutoSignJobCondition = (index: number) => {
    setConditions((current) => current.map((condition, conditionIndex) =>
      conditionIndex === index
        ? { ...condition, operation: condition.propertyName === 'invoiceDate' ? '=' : '', value: '' }
        : condition,
    ));
  };
  const updateAutoSignTimeWindowError = (_value: any, item: any) => {
    setAutoSignTimeWindowError(
      item.autoSign === true && hasAutoSignJobEndBeforeStart(item)
        ? t('system.autoSignEndTimeBeforeStart')
        : '',
    );
  };
  const validateAutoSignJob = async (item: any, excludedId?: string) => {
    if (item.autoSign !== true) return true;

    // Khi bật ký tự động, yêu cầu chọn ký hiệu cụ thể trước khi tạo hoặc cập nhật job.
    if (!item.serial) {
      ToastService.error(t('validation.required', { field: t('common.symbol') }));
      return false;
    }

    if (hasAutoSignJobEndBeforeStart(item)) {
      setAutoSignTimeWindowError(t('system.autoSignEndTimeBeforeStart'));
      ToastService.error(t('system.autoSignEndTimeBeforeStart'));
      return false;
    }
    setAutoSignTimeWindowError('');

    // Ngày hóa đơn luôn có toán tử "="; ngày tạo được coi là đang dùng khi người dùng chọn toán tử hoặc nhập giá trị.
    const usedConditions = conditions.filter((condition) =>
      condition.propertyName === 'invoiceDate'
        ? Boolean(condition.value)
        : Boolean(condition.operation || condition.value),
    );
    if (usedConditions.some((condition) =>
      !condition.propertyName || !condition.value ||
      (condition.propertyName !== 'invoiceDate' && !condition.operation),
    )) {
      ToastService.error(t('system.autoSignConditionIncomplete'));
      return false;
    }
    if (usedConditions.some((condition) =>
      condition.propertyName === 'creationTime' && !/^(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d$/.test(condition.value),
    )) {
      ToastService.error(t('system.autoSignCreationTimeInvalid'));
      return false;
    }
    if (usedConditions.some((condition) =>
      condition.propertyName === 'invoiceDate' && !['today', 'yesterday'].includes(condition.value),
    )) {
      ToastService.error(t('system.autoSignInvoiceDateInvalid'));
      return false;
    }

    const { items = [] } = await Service.getListJob({ autoSign: true, maxResultCount: 1000 });
    if (hasAutoSignJobOverlappingWindow(item, items, excludedId)) {
      ToastService.error(t('system.autoSignTimeOverlap'));
      return false;
    }
    return true;
  };
  const {
    render: renderDialogCrup,
    create,
    update,
    clone,
    getValue,
    setValue
  } = useDialogCrup({
    header,
    dataKey,
    width: '80rem',
    fields: [
      {
        field: 'emailTo',
        header: t('system.notificationEmail'),
        required: true,
        InputTextProps: {
          maxLength: 250,
          placeholder: t('system.emailSeparatorHint'),
          validator: 'multipleEmail',
        },
        className: 'md:col-12',
      },

      {
        field: 'autoSign',
        type: 'Dropdown',
        onValueChange: updateAutoSignTimeWindowError,
        className: 'md:col-6',
        header: t('common.businessOperation'),
        DropdownProps: { options: [{ value: false, label: t('system.sendNotificationEmail') }, { value: true, label: t('system.autoSignInvoice') }] },
      },
      {
        field: 'serial',
        header: t('common.symbol'),
        required: tempAutoSign === true,
        type: 'Dropdown',
        DropdownProps: { filter: true, options: symbol, disabled: isSerialDisable ? isSerialDisable : !tempAutoSign, placeholder: t('common.choose') },
        className: 'md:col-6',
      },
      {
        field: 'timeRun',
        header: t('system.startTime'),
        required: true,
        type: 'Dropdown',
        onValueChange: updateAutoSignTimeWindowError,
        DropdownProps: { options: timeRunOptions },
        className: 'md:col-6',
      },
      {
        field: 'endtHour',
        header: t('system.endTime'),
        type: 'Dropdown',
        displayDependency: (item) => item.autoSign === true,
        onValueChange: updateAutoSignTimeWindowError,
        errorMessage: autoSignTimeWindowError || undefined,
        DropdownProps: { options: endHourOptions, placeholder: t('system.defaultEndTime'), showClear: true },
        className: 'md:col-6',
      },
      {
        field: 'minuteNextStep',
        header: tempAutoSign ? t('system.signAgainTime') : t('system.resendNotificationTime'),
        type: 'Dropdown',
        DropdownProps: {
          options: renderOptions(),
        },
        className: 'md:col-6',
      },
      {
        field: 'conditionFilters',
        type: 'custom',
        displayDependency: (item) => item.autoSign === true,
        body: (
          <div className="my-3 w-full">
            <h4 className="m-0 mb-2 text-lg">{t('system.autoSignConditionTitle')}</h4>
            <div className="border-1 surface-border">
              <div className="grid m-0 align-items-center surface-ground border-bottom-1 surface-border text-sm font-semibold">
                <div className="col-12 md:col-4 p-2 border-right-1 surface-border">{t('system.fieldName')}</div>
                <div className="col-12 md:col-3 p-2 border-right-1 surface-border">{t('system.condition')}</div>
                <div className="col-12 md:col-4 p-2 border-right-1 surface-border">{t('system.value')}</div>
                <div className="col-12 md:col-1 p-2" />
              </div>
              {conditions.map((condition, index) => (
                <div
                  className={`grid m-0 align-items-center ${index === 0 ? 'border-bottom-1 surface-border' : ''}`}
                  key={`auto-sign-condition-${index}`}
                >
                  <div className="col-12 md:col-4 p-1 border-right-1 surface-border">
                    <div className="px-2 py-2">
                      {conditionFieldOptions.find((option) => option.value === condition.propertyName)?.label || condition.propertyName}
                    </div>
                  </div>
                  <div className="col-12 md:col-3 p-1 border-right-1 surface-border">
                    {condition.propertyName === 'invoiceDate' ? (
                      <div className="px-2 py-2 text-700">{t('common.equals')}</div>
                    ) : (
                      <Dropdown
                        value={condition.operation}
                        options={conditionOperationOptions}
                        onChange={(event) => updateAutoSignJobCondition(index, 'operation', event.value)}
                        placeholder={t('common.choose')}
                        className="w-full"
                      />
                    )}
                  </div>
                  <div className="col-12 md:col-4 p-1 border-right-1 surface-border">
                    {condition.propertyName === 'invoiceDate' ? (
                      <Dropdown
                        value={condition.value}
                        options={invoiceDateValueOptions}
                        onChange={(event) => updateAutoSignJobCondition(index, 'value', event.value)}
                        placeholder={t('common.choose')}
                        className="w-full"
                      />
                    ) : (
                      <AutoSignJobConditionTimePicker
                        value={condition.value}
                        onChange={(value) => updateAutoSignJobCondition(index, 'value', value)}
                      />
                    )}
                  </div>
                  <div className="col-12 md:col-1 p-1 flex justify-content-center">
                    <Button
                      type="button"
                      icon="pi pi-trash"
                      title={t('common.clear')}
                      onClick={() => clearAutoSignJobCondition(index)}
                      className="p-button-sm p-button-rounded p-button-outlined p-button-danger"
                      disabled={condition.propertyName === 'invoiceDate'
                        ? !condition.value
                        : !condition.operation && !condition.value}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ),
      },
      {
        field: "isUse",
        type: 'Checkbox',
        header: t('catalog.emailUsage'),
        className: "md:col-6"
      },
      {
        field: "customText",
        type: "custom",
        body: (
          <div className="flex flex-column">
            <p className="font-italic font-bold text-lg">{t('system.conditionLabel')}</p>
            <p className="font-italic">1. {t('system.certificateCondition')}</p>
            <p className="font-italic">2. {t('system.invoiceStatusCondition')}</p>
          </div>
        ),
        displayDependency: (item) => item.autoSign === true,
      },
    ],
    defaultValue: {
      minuteNextStep: tempAutoSign ? 5 : 30,
      autoSign: false,
      isUse: true
    },
    createItem: (item) => {
      return validateAutoSignJob(item).then((isValid) => {
        if (!isValid) return Promise.reject(ASYNC_FLOW_CANCELLED);
        return Service.createJob({
          ...item,
          endtHour: item.autoSign ? serializeAutoSignJobEndHour(item.endtHour) : null,
          condition: item.autoSign ? buildAutoSignJobCondition(conditions) : undefined,
        });
      });
    },
    updateItem: (id, item) => {
      return validateAutoSignJob(item, id).then((isValid) => {
        if (!isValid) return Promise.reject(ASYNC_FLOW_CANCELLED);
        return Service.updateJob({
          ...item,
          id,
          endtHour: item.autoSign ? serializeAutoSignJobEndHour(item.endtHour) : null,
          condition: item.autoSign ? buildAutoSignJobCondition(conditions) : undefined,
        });
      });
    },
    refreshLazyData,
  });
  useEffect(() => {
    const values = getValue()
    setTempAutoSign(values.autoSign)
  }, [getValue])
  useEffect(() => {
    const data = getValue()
    if (!renderOptions().find(option => option.value === data.minuteNextStep)) {
      data.minuteNextStep = defaultValue
      setValue(data)
    }
    if (!tempAutoSign) {
      if (data.serial) {
        setTempSerial(data.serial)
      }
      data.serial = null
      setValue(data)
    }
    if (tempAutoSign && tempSerial) {
      data.serial = tempSerial
      setValue(data)
    }
  }, [defaultValue, renderOptions, tempAutoSign, tempSerial]) // eslint-disable-line react-hooks/exhaustive-deps
  const { renderToolbar } = useToolbar({
    dataKey,
    create: () => {
      setAutoSignTimeWindowError('');
      setTempSerial(null)
      setIsSerialDisable(null)
      setConditions(createAutoSignJobDefaultConditions());
      create()
    },
    update: (item) => {
      setAutoSignTimeWindowError(
        item.autoSign === true && hasAutoSignJobEndBeforeStart({
          ...item,
          endtHour: getAutoSignJobEndHourValue(item.endtHour),
        })
          ? t('system.autoSignEndTimeBeforeStart')
          : '',
      );
      setIsSerialDisable(true)
      setConditions(normalizeAutoSignJobConditions(item.condition));
      update({ ...item, endtHour: getAutoSignJobEndHourValue(item.endtHour) })
    },
    clone: (item) => {
      setAutoSignTimeWindowError(
        item.autoSign === true && hasAutoSignJobEndBeforeStart({
          ...item,
          endtHour: getAutoSignJobEndHourValue(item.endtHour),
        })
          ? t('system.autoSignEndTimeBeforeStart')
          : '',
      );
      setConditions(normalizeAutoSignJobConditions(item.condition));
      clone({ ...item, endtHour: getAutoSignJobEndHourValue(item.endtHour) });
    },
    reloadLazyData,
    selectedItems,
    deleteItem: (id) => {
      return Service.deleteJob(id);
    },
  });

  return (
    <Fragment>
      {renderToolbar()}
      {renderDataTable()}
      {renderDialogCrup()}
    </Fragment>
  );
};
